import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Alert } from 'react-bootstrap';
import { apiFetch } from '../api';
import DetailFiles from './DetailFiles';

function StageEditModal({ show, onHide, stage, contractId, onSaved, initialStatus }) {
  const [form, setForm] = useState({
    name: '',
    quantity: '',
    price_per_unit: '',
    material: '',
    blank_type: '',
    dimension_per_unit: '',
    dimension_total: '',
    status: 'pending',
    deadline: '',
    description: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [deleteImage, setDeleteImage] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (show && stage) {
      setForm({
        name: stage.name || '',
        quantity: stage.quantity ?? '',
        price_per_unit: stage.price_per_unit ?? '',
        material: stage.material || '',
        blank_type: stage.blank_type || '',
        dimension_per_unit: stage.dimension_per_unit || '',
        dimension_total: stage.dimension_total || '',
        status: initialStatus || stage.status || 'pending',
        deadline: stage.deadline || '',
        description: stage.description || '',
      });
      if (stage.image) {
        setImagePreview(stage.image);
      } else {
        setImagePreview(null);
      }
      setImageFile(null);
      setDeleteImage(false);
      setError('');
    }
  }, [show, stage, initialStatus]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleQuantityChange = (value) => {
    const num = parseInt(value, 10);
    if (!isNaN(num) && num >= 0) {
      setForm((prev) => ({ ...prev, quantity: num }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setDeleteImage(false);
    }
  };

  const handleRemoveImage = () => {
    setDeleteImage(true);
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // Удаление изображения
      if (deleteImage) {
        await apiFetch(`/api/details/${stage.id}/`, {
          method: 'PATCH',
          body: JSON.stringify({ image: null }),
        });
        onSaved();
        onHide();
        setSaving(false);
        return;
      }

      // Загрузка нового изображения (FormData)
      if (imageFile) {
        const formData = new FormData();
        formData.append('name', form.name.trim());
        formData.append('quantity', Number(form.quantity) || 0);
        formData.append('price_per_unit', Number(form.price_per_unit) || 0);
        formData.append('material', form.material.trim());
        formData.append('blank_type', form.blank_type.trim());
        formData.append('dimension_per_unit', form.dimension_per_unit.trim());
        formData.append('dimension_total', form.dimension_total.trim());
        formData.append('status', form.status);
        formData.append('deadline', form.deadline || '');
        formData.append('description', form.description.trim());
        formData.append('image', imageFile);

        // Используем apiFetch с FormData
        await apiFetch(`/api/details/${stage.id}/`, {
          method: 'PATCH',
          body: formData,
        });
        onSaved();
        onHide();
        setSaving(false);
        return;
      }

      // Обычный PATCH без файла
      const payload = {};
      if (form.name !== stage.name) payload.name = form.name.trim();
      if (Number(form.quantity) !== stage.quantity) payload.quantity = Number(form.quantity) || 0;
      if (Number(form.price_per_unit) !== stage.price_per_unit) payload.price_per_unit = Number(form.price_per_unit) || 0;
      if (form.status !== stage.status) payload.status = form.status;
      if (form.deadline !== (stage.deadline || '')) payload.deadline = form.deadline || null;
      if (form.description !== (stage.description || '')) payload.description = form.description.trim();
      if (form.material !== stage.material) payload.material = form.material.trim();
      if (form.blank_type !== stage.blank_type) payload.blank_type = form.blank_type.trim();
      if (form.dimension_per_unit !== stage.dimension_per_unit) payload.dimension_per_unit = form.dimension_per_unit.trim();
      if (form.dimension_total !== stage.dimension_total) payload.dimension_total = form.dimension_total.trim();
      if (Object.keys(payload).length === 0) {
        onHide();
        setSaving(false);
        return;
      }
      await apiFetch(`/api/details/${stage.id}/`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      onSaved();
      onHide();
    } catch (err) {
      console.error('Ошибка сохранения:', err);
      setError(err.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton className="app-surface">
        <Modal.Title>Редактировать деталь</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="app-surface">
          {error && <Alert variant="danger">{error}</Alert>}

          <Form.Group className="mb-3">
            <Form.Label>Наименование</Form.Label>
            <Form.Control
              type="text"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Количество</Form.Label>
            <div className="d-flex align-items-center gap-3">
              <Form.Control
                type="number"
                step="1"
                min="0"
                value={form.quantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
                style={{ width: '120px' }}
              />
              <Form.Range
                min="0"
                max="1000"
                step="1"
                value={form.quantity || 0}
                onChange={(e) => handleQuantityChange(e.target.value)}
                style={{ flex: 1 }}
              />
              <span className="text-muted small" style={{ width: '60px', textAlign: 'center' }}>
                {Math.round(form.quantity || 0)}
              </span>
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Цена за единицу, ₽</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              value={form.price_per_unit}
              onChange={(e) => handleChange('price_per_unit', e.target.value)}
              placeholder="0.00"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Материал</Form.Label>
            <Form.Control
              type="text"
              value={form.material}
              onChange={(e) => handleChange('material', e.target.value)}
              placeholder="Например: Сталь 45"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Вид заготовки</Form.Label>
            <Form.Control
              type="text"
              value={form.blank_type}
              onChange={(e) => handleChange('blank_type', e.target.value)}
              placeholder="Например: Пруток, лист, поковка"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Размер на 1 шт</Form.Label>
            <Form.Control
              type="text"
              value={form.dimension_per_unit}
              onChange={(e) => handleChange('dimension_per_unit', e.target.value)}
              placeholder="Например: 100x50x20 мм"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Размер на общее кол-во</Form.Label>
            <Form.Control
              type="text"
              value={form.dimension_total}
              onChange={(e) => handleChange('dimension_total', e.target.value)}
              placeholder="Например: 2000x1000x5 мм"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Статус</Form.Label>
            <Form.Select
              value={form.status}
              onChange={(e) => handleChange('status', e.target.value)}
            >
              <option value="pending">Ожидание</option>
              <option value="in_progress">В работе</option>
              <option value="completed">Выполнено</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Срок</Form.Label>
            <Form.Control
              type="date"
              value={form.deadline}
              onChange={(e) => handleChange('deadline', e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Описание</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Дополнительная информация"
            />
          </Form.Group>

          {/* Поле загрузки изображения */}
          <Form.Group className="mb-3">
            <Form.Label>Изображение (фон карточки)</Form.Label>
            <div className="d-flex flex-column gap-2">
              {imagePreview && (
                <div style={{ position: 'relative', maxWidth: '200px' }}>
                  <img
                    src={imagePreview}
                    alt="Превью"
                    style={{ width: '100%', height: 'auto', borderRadius: '6px' }}
                  />
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    style={{ position: 'absolute', top: '4px', right: '4px', borderRadius: '50%' }}
                    onClick={handleRemoveImage}
                  >
                    ×
                  </button>
                </div>
              )}
              <div>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="form-control"
                />
                <small className="text-muted">Рекомендуемый размер: 400×300 px</small>
              </div>
            </div>
          </Form.Group>

          {stage && (
            <div className="mt-3">
              <Form.Label>Прикреплённые файлы</Form.Label>
              <DetailFiles detailId={stage.id} showUpload={true} compact={false} />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="app-surface">
          <button type="button" className="app-btn" onClick={onHide}>Отмена</button>
          <button type="submit" className="app-btn app-btn-primary" disabled={saving}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default StageEditModal;